/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import get from 'lodash/get';
import {
    CalendarDate,
    DateFormatter,
    parseAbsolute,
    ZonedDateTime,
} from '@internationalized/date';
import { isValidDateFormat, parseDate } from 'ui-activitypass/src/utils/date_time_utils';
import { getParentInventory } from '../api';
import * as constants from '../constants';

const weekdayFormatter = new DateFormatter('en-US', {
    weekday: 'short', // 'Fri'
});

const dateFormatterShortMonth = new DateFormatter('en-US', {
    month: 'short', //  'Jun'
    day: 'numeric', // '9'
    year: 'numeric', // '2014'
});

// 05/1/2024
const dateTimeFormatter2DigitMonth = new DateFormatter('en-US', {
    month: '2-digit', // '6'
    day: 'numeric', // '9'
    year: 'numeric', // '2014'
});
export const formatDate = (date) => {
    if (!date) return false;
    const parsedDate = date instanceof CalendarDate ? date : parseDate(date);
    return dateTimeFormatter2DigitMonth.format(parsedDate.toDate());
};
// use this to replace moment formatting 'ddd, ll'
export const formatDateShortWithWeekDay = (date) => {
    if (!date) return false;
    const parsedDate = (date instanceof CalendarDate ? date : parseDate(date)).toDate();
    return `${weekdayFormatter.format(parsedDate)}, ${dateFormatterShortMonth.format(parsedDate)}`;
};

const dateTimeFormatterShortMonth = new DateFormatter('en-US', {
    month: 'short', //  'Jun'
    day: 'numeric', // '9'
    year: 'numeric', // '2014'
    hour: '2-digit', // '9'
    minute: '2-digit', // '44'
});
export const formatDateTimeShortWithWeekDay = (date) => {
    if (!date) return false;
    const parsedDate = (
        date instanceof ZonedDateTime ? date : parseAbsolute(date)
    ).toDate();
    return `${weekdayFormatter.format(parsedDate)}, ${dateTimeFormatterShortMonth.format(parsedDate)}`;
};

// use this to replace moment formatting 'll'
export const formatDateShort = (date) => {
    if (!date) return false;
    const parsedDate = (date instanceof CalendarDate ? date : parseDate(date)).toDate();
    return `${weekdayFormatter.format(parsedDate)}, ${dateTimeFormatterShortMonth.format(parsedDate)}`;
};

export const getUpsellDates = (reservation) => {
    const invType = reservation.inventory_type;
    const getDateStringOrEmpty = (date) =>
        isValidDateFormat(date) ? parseDate(date).toString() : '';
    switch (invType) {
        case 'ACTIVITYPASS':
            return {
                start_date: getDateStringOrEmpty(
                    get(reservation, 'order_details.valid_from')
                ),
                end_date: getDateStringOrEmpty(
                    get(reservation, 'order_details.valid_to')
                ),
            };
        case 'CAMPING':
            return {
                start_date: getDateStringOrEmpty(
                    get(reservation, 'order_details.check_in')
                ),
                end_date: getDateStringOrEmpty(
                    get(reservation, 'order_details.check_out')
                ),
            };
        case 'PERMIT':
            return {
                start_date: getDateStringOrEmpty(
                    get(reservation, 'order_details.entry_date')
                ),
                end_date: getDateStringOrEmpty(
                    get(reservation, 'order_details.exit_date')
                ),
            };
        case 'TICKET':
            return {
                start_date: getDateStringOrEmpty(
                    get(reservation, 'order_details.tour_date')
                ),
                end_date: getDateStringOrEmpty(
                    get(reservation, 'order_details.tour_date')
                ),
            };
        case 'TIMED_ENTRY':
            return {
                start_date: getDateStringOrEmpty(
                    get(reservation, 'order_details.tour_date')
                ),
                end_date: getDateStringOrEmpty(
                    get(reservation, 'order_details.tour_date')
                ),
            };
        case 'VEHICLE_PERMIT':
            return {
                start_date: getDateStringOrEmpty(
                    get(reservation, 'order_details.start_time')
                ),
                end_date: getDateStringOrEmpty(
                    get(reservation, 'order_details.end_time')
                ),
            };
        case 'VENUE_RESERVATIONS':
            return {
                start_date: getDateStringOrEmpty(
                    get(reservation, 'order_details.event_date')
                ),
                end_date: getDateStringOrEmpty(
                    get(reservation, 'order_details.event_date')
                ),
            };
        default:
            return { start_date: null, end_date: null };
    }
};

export const getActivityPassReminderParams = async (reservations, flag) => {
    if (flag) {
        const facilitiesCache = {};
        const facilityIdsRequests = reservations.map(async (item) => {
            if (item.inventory_type === constants.INVENTORY_PARKPASS) {
                return {
                    facility_id: item?.order_details?.facility_id,
                    reservation_id: item?.reservation_id,
                };
            }

            try {
                if (!facilitiesCache[item?.order_details?.facility_id]) {
                    const locationId = item?.order_details?.facility_id
                        ? item?.order_details?.facility_id
                        : item?.order_details?.location_id;
                    facilitiesCache[item?.order_details?.facility_id] =
                        getParentInventory(locationId);
                }

                const parent = await facilitiesCache[item?.order_details?.facility_id];
                return {
                    facility_id: parent?.data?.internal_parent_location_id,
                    reservation_id: item?.reservation_id,
                };
            } catch {
                return {
                    facility_id: '',
                    reservation_id: '',
                };
            }
        });

        const facilityIds = await Promise.all(facilityIdsRequests);
        const params = reservations.map((item) => {
            if (item.inventory_type === constants.INVENTORY_PARKPASS) {
                return {
                    facility_id: item.order_details.facility_id,
                    parent_location_id: item.order_details.facility_id,
                    inventory_type: item.inventory_type,
                    start_date: null,
                    end_date: null,
                };
            }
            const { start_date, end_date } = getUpsellDates(item);
            const parentId = facilityIds?.find(
                (facility) => facility.reservation_id === item.reservation_id
            );
            return {
                inventory_type: item.inventory_type,
                parent_location_id: parentId?.facility_id ? parentId?.facility_id : '',
                start_date,
                end_date,
            };
        });
        return params;
    }
    return [];
};
